import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import TechnicianBuildingDevicesBlock from '../../../components/technician/TechnicianBuildingDevicesBlock';
import { apiRequest, downloadApiFile, openApiFile } from '../../../api/client';
import '../../admin/quotes/quote-generator.css';
import '../technician.css';

type WorkOrderLite = {
    _id: string;
    title?: string;
    status?: string;
    priority?: string;
    instructions?: string;
    formTemplateId?: string;
    inspectionId?: string | { _id?: string };
    clientId?: string | { _id?: string };
    client?: { name?: string; address?: string; phone?: string; email?: string };
    documents?: Array<{ name: string; url: string }>;
    schedule?: { date?: string; startTime?: string; endTime?: string };
};

type InspectionRow = {
    _id: string;
    siteAddress?: string;
    status?: string;
    frequency?: string;
    standardCode?: string;
    createdAt?: string;
    completedAt?: string;
};

function workOrderInspectionOid(wo: WorkOrderLite | null): string | null {
    if (!wo?.inspectionId) return null;
    const linked = wo.inspectionId;
    if (typeof linked === 'string' && linked.trim()) return linked.trim();
    if (typeof linked === 'object' && linked !== null && '_id' in linked && (linked as { _id?: string })._id) {
        return String((linked as { _id: string })._id);
    }
    return null;
}

function workOrderClientOid(wo: WorkOrderLite | null): string | null {
    if (!wo?.clientId) return null;
    const c = wo.clientId;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (typeof c === 'object' && c !== null && '_id' in c && (c as { _id?: string })._id) {
        return String((c as { _id: string })._id);
    }
    return null;
}

export default function TechJobHub(): React.JSX.Element {
    const navigate = useNavigate();
    const { workOrderId } = useParams<{ workOrderId: string }>();
    const [tab, setTab] = useState<'details' | 'checklist' | 'devices' | 'reports'>('details');
    const [wo, setWo] = useState<WorkOrderLite | null>(null);
    const [jobInspection, setJobInspection] = useState<InspectionRow | null>(null);
    const [completedReports, setCompletedReports] = useState<InspectionRow[]>([]);
    const [reportsBusy, setReportsBusy] = useState(false);
    const [downloading, setDownloading] = useState<string | null>(null);
    const [openingDoc, setOpeningDoc] = useState<string | null>(null);
    const [loadErr, setLoadErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(true);

    const clientOid = workOrderClientOid(wo);
    const jobBackPath = workOrderId ? `/technician/jobs/${workOrderId}` : '/technician/JobList';

    const loadJobInspection = useCallback(async (workOrder: WorkOrderLite, resolved?: InspectionRow | null) => {
        if (resolved) {
            setJobInspection(resolved);
            return;
        }

        const linkedId = workOrderInspectionOid(workOrder);
        if (linkedId) {
            try {
                const res = await apiRequest<{ success?: boolean; data: InspectionRow }>(
                    `/api/inspections/${linkedId}`
                );
                setJobInspection(res.data || null);
                return;
            } catch {
                setJobInspection(null);
                return;
            }
        }

        if (workOrder._id) {
            try {
                const res = await apiRequest<{ success?: boolean; data: InspectionRow[] }>(
                    `/api/inspections?workOrderId=${encodeURIComponent(workOrder._id)}`
                );
                const rows = Array.isArray(res.data) ? res.data : [];
                if (rows.length) {
                    const best = rows.sort((a, b) => {
                        const bt = new Date(b.completedAt || b.createdAt || 0).getTime();
                        const at = new Date(a.completedAt || a.createdAt || 0).getTime();
                        return bt - at;
                    })[0];
                    setJobInspection(best);
                    return;
                }
            } catch {
                /* try building fallback below */
            }
        }

        const clientId = workOrderClientOid(workOrder);
        if (clientId) {
            try {
                const res = await apiRequest<{ success?: boolean; data: InspectionRow[] }>(
                    `/api/inspections?clientId=${encodeURIComponent(clientId)}&status=in_progress`
                );
                const rows = Array.isArray(res.data) ? res.data : [];
                if (rows.length) {
                    const best = rows.sort((a, b) => {
                        const bt = new Date(b.createdAt || 0).getTime();
                        const at = new Date(a.createdAt || 0).getTime();
                        return bt - at;
                    })[0];
                    setJobInspection(best);
                    return;
                }
            } catch {
                setJobInspection(null);
                return;
            }
        }

        setJobInspection(null);
    }, []);

    const refreshJob = useCallback(async () => {
        if (!workOrderId) return;
        setLoadErr(null);
        const res = await apiRequest<{ data: WorkOrderLite; inspection?: InspectionRow | null }>(
            `/api/work-orders/${workOrderId}`
        );
        const workOrder = res.data;
        setWo(workOrder);
        await loadJobInspection(workOrder, res.inspection ?? null);
    }, [workOrderId, loadJobInspection]);

    const loadCompletedReports = useCallback(async (cid: string) => {
        setReportsBusy(true);
        try {
            const res = await apiRequest<{ success?: boolean; data: InspectionRow[] }>(
                `/api/inspections?clientId=${encodeURIComponent(cid)}&status=completed`
            );
            const rows = (Array.isArray(res.data) ? res.data : []).sort((a, b) => {
                const bt = new Date(b.completedAt || b.createdAt || 0).getTime();
                const at = new Date(a.completedAt || a.createdAt || 0).getTime();
                return bt - at;
            });
            setCompletedReports(rows);
        } catch {
            setCompletedReports([]);
        } finally {
            setReportsBusy(false);
        }
    }, []);

    useEffect(() => {
        if (!workOrderId) return;
        setBusy(true);
        refreshJob()
            .catch(() => setLoadErr('Failed to load work order.'))
            .finally(() => setBusy(false));
    }, [workOrderId, refreshJob]);

    useEffect(() => {
        if (tab !== 'checklist' || !workOrderId) return;
        refreshJob().catch(() => undefined);
    }, [tab, workOrderId, refreshJob]);

    useEffect(() => {
        if (!clientOid) {
            setCompletedReports([]);
        }
    }, [clientOid]);

    useEffect(() => {
        if (tab !== 'reports' || !clientOid) return;
        void loadCompletedReports(clientOid);
    }, [tab, clientOid, loadCompletedReports]);

    async function downloadReportPdf(id: string) {
        if (downloading) return;
        setDownloading(id);
        try {
            await downloadApiFile(`/api/inspections/${id}/pdf`, `Inspection-${id}.pdf`);
        } catch {
            setLoadErr('Failed to download PDF.');
        } finally {
            setDownloading(null);
        }
    }

    async function openDocument(doc: { name: string; url: string }) {
        const key = doc.url || doc.name;
        if (openingDoc) return;
        setOpeningDoc(key);
        try {
            await openApiFile(doc.url, doc.name || 'document.pdf');
        } catch (error) {
            setLoadErr(error instanceof Error ? error.message : 'Failed to open document.');
        } finally {
            setOpeningDoc(null);
        }
    }

    if (!workOrderId) {
        return (
            <AppShell>
                <div className="tech-page">
                    <p className="tech-sub">Missing job id.</p>
                    <Link to="/technician/JobList" className="tech-btn tech-btn-primary" style={{ display: 'inline-block', marginTop: 12 }}>
                        Back to jobs
                    </Link>
                </div>
            </AppShell>
        );
    }

    return (
        <AppShell>
            <div className="tech-page">
                <header className="tech-hero">
                    <div>
                        <button
                            type="button"
                            className="tech-btn tech-btn-ghost"
                            style={{ marginBottom: 8 }}
                            onClick={() => navigate('/technician/JobList')}
                        >
                            ← Job list
                        </button>
                        <p className="tech-kicker">Work order</p>
                        <h1 className="tech-title">{busy ? 'Loading…' : wo?.title || 'Assigned job'}</h1>
                        <p className="tech-sub">
                            {wo?.client?.address || wo?.client?.name || ''}
                        </p>
                        {!busy && wo ? (
                            <p className="tech-sub" style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                <span className="tech-badge ok">{wo.priority || 'Normal'}</span>
                                <span className="tech-badge ok">{wo.status || '—'}</span>
                                {wo.schedule?.date ? (
                                    <span style={{ color: 'var(--tech-muted)' }}>
                                        {new Date(wo.schedule.date).toLocaleDateString()}
                                    </span>
                                ) : null}
                            </p>
                        ) : null}
                    </div>
                </header>

                {loadErr ? <div className="banner banner-error" style={{ marginBottom: 16 }}>{loadErr}</div> : null}

                {!clientOid && !busy ? (
                    <div className="tech-card" style={{ padding: 20 }}>
                        <p style={{ margin: 0, color: '#b45309', lineHeight: 1.5 }}>
                            This job isn&apos;t linked to a Client record yet. Ask an administrator to associate the building on
                            the work order so devices and inspections load here.
                        </p>
                    </div>
                ) : null}

                <div role="tablist" aria-label="Job workflow" className="tech-tabbar" style={{ maxWidth: '100%' }}>
                    <button type="button" role="tab" aria-selected={tab === 'details'} className={`tech-tabbar__tab${tab === 'details' ? ' is-active' : ''}`} onClick={() => setTab('details')}>Details</button>
                    <button type="button" role="tab" aria-selected={tab === 'checklist'} className={`tech-tabbar__tab${tab === 'checklist' ? ' is-active' : ''}`} onClick={() => setTab('checklist')}>Checklist</button>
                    <button type="button" role="tab" aria-selected={tab === 'devices'} className={`tech-tabbar__tab${tab === 'devices' ? ' is-active' : ''}`} onClick={() => setTab('devices')}>Devices</button>
                    <button type="button" role="tab" aria-selected={tab === 'reports'} className={`tech-tabbar__tab${tab === 'reports' ? ' is-active' : ''}`} onClick={() => setTab('reports')}>Reports</button>
                </div>

                {tab === 'details' ? (
                    <div className="wo-details-card">
                        <h3>Job details</h3>
                        <p><strong>{wo?.title || 'Assigned job'}</strong></p>
                        {wo?.instructions ? <p>{wo.instructions}</p> : <p>No special instructions from admin.</p>}
                        {wo?.client?.name ? <p>{wo.client.name}</p> : null}
                        {wo?.client?.address ? <p>{wo.client.address}</p> : null}
                        {wo?.client?.phone ? <p>Phone: {wo.client.phone}</p> : null}
                        {wo?.client?.email ? <p>Email: {wo.client.email}</p> : null}
                        <h3 style={{ marginTop: 20 }}>Documents & blueprints</h3>
                        {!wo?.documents?.length ? (
                            <p className="tech-sub">No documents uploaded.</p>
                        ) : (
                            <div className="tech-doc-list">
                                {wo.documents.map((doc) => {
                                    const key = doc.url || doc.name;
                                    const busy = openingDoc === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            className="tech-doc"
                                            disabled={busy}
                                            onClick={() => void openDocument(doc)}
                                        >
                                            {busy ? 'Opening…' : `📄 ${doc.name}`}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : null}

                {tab === 'devices' ?
                    <TechnicianBuildingDevicesBlock clientId={clientOid} />
                : tab === 'checklist' ? (
                    <>
                        {!clientOid ? null : !jobInspection ?
                            <div className="tech-card" style={{ padding: 20 }}>
                                <p style={{ margin: 0, color: 'var(--tech-muted)', lineHeight: 1.5 }}>
                                    No inspection checklist is linked to this job yet. Ask your administrator to schedule
                                    an inspection for this visit.
                                </p>
                            </div>
                        : jobInspection.status === 'completed' ? (
                            <div className="tech-card" style={{ padding: 24 }}>
                                <p className="tech-kicker" style={{ marginBottom: 8 }}>Submitted report</p>
                                <h3 style={{ margin: '0 0 8px' }}>
                                    {jobInspection.siteAddress || wo?.client?.name || 'This building'}
                                </h3>
                                <p style={{ margin: '0 0 16px', color: 'var(--tech-muted)' }}>
                                    {(jobInspection.frequency || 'annual')} · {(jobInspection.standardCode || 'ULC_S536')}
                                    {jobInspection.completedAt || jobInspection.createdAt
                                        ? ` · Completed ${new Date(jobInspection.completedAt || jobInspection.createdAt || '').toLocaleDateString()}`
                                        : ''}
                                </p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                    <Link
                                        to={`/technician/inspections/${jobInspection._id}/summary`}
                                        state={{ backPath: jobBackPath }}
                                        className="tech-btn tech-btn-primary"
                                    >
                                        View summary
                                    </Link>
                                    <button
                                        type="button"
                                        className="tech-btn tech-btn-ghost"
                                        disabled={downloading === jobInspection._id}
                                        onClick={() => void downloadReportPdf(jobInspection._id)}
                                    >
                                        {downloading === jobInspection._id ? 'Downloading…' : 'Download PDF'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="tech-card" style={{ padding: 24 }}>
                                <p className="tech-kicker" style={{ marginBottom: 8 }}>Active checklist</p>
                                <h3 style={{ margin: '0 0 8px' }}>
                                    {jobInspection.siteAddress || wo?.client?.name || 'This building'}
                                </h3>
                                <p style={{ margin: '0 0 16px', color: 'var(--tech-muted)' }}>
                                    {(jobInspection.frequency || 'annual')} · {(jobInspection.standardCode || 'ULC_S536')}
                                </p>
                                <Link
                                    to={`/technician/inspections/${jobInspection._id}/checklist`}
                                    state={{ backPath: jobBackPath }}
                                    className="tech-btn tech-btn-primary"
                                >
                                    Open checklist
                                </Link>
                            </div>
                        )}
                    </>
                ) : tab === 'reports' ? (
                    <>
                        {!clientOid ? null : reportsBusy ?
                            <div className="tech-card" style={{ padding: 20 }}>
                                <p style={{ margin: 0, color: 'var(--tech-muted)' }}>Loading reports…</p>
                            </div>
                        : completedReports.length === 0 ?
                            <div className="tech-card" style={{ padding: 20 }}>
                                <p style={{ margin: 0, color: 'var(--tech-muted)' }}>
                                    No completed inspection reports for this building yet.
                                </p>
                            </div>
                        : (
                            <div className="tech-card" style={{ padding: 0, overflow: 'hidden' }}>
                                <div className="list-responsive">
                                    <div className="list-table-pane">
                                        <table className="tech-table">
                                            <thead>
                                                <tr>
                                                    <th>Site</th>
                                                    <th>Completed</th>
                                                    <th>Standard</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {completedReports.map((insp) => (
                                                    <tr key={insp._id}>
                                                        <td>{insp.siteAddress || wo?.client?.name || `…${insp._id.slice(-6)}`}</td>
                                                        <td>
                                                            {insp.completedAt || insp.createdAt
                                                                ? new Date(insp.completedAt || insp.createdAt || '').toLocaleDateString()
                                                                : '—'}
                                                        </td>
                                                        <td>{insp.standardCode || '—'}</td>
                                                        <td style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                            <button
                                                                type="button"
                                                                className="tech-btn tech-btn-primary"
                                                                style={{ fontSize: 12, padding: '6px 12px' }}
                                                                disabled={downloading === insp._id}
                                                                onClick={() => void downloadReportPdf(insp._id)}
                                                            >
                                                                {downloading === insp._id ? '…' : 'PDF'}
                                                            </button>
                                                            <Link
                                                                to={`/technician/inspections/${insp._id}/summary`}
                                                                state={{ backPath: jobBackPath }}
                                                                className="tech-btn tech-btn-ghost"
                                                                style={{ fontSize: 12, padding: '6px 12px' }}
                                                            >
                                                                Summary
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </AppShell>
    );
}
